import React, { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, User, Search, Loader2 } from "lucide-react";
import { useLiveChat } from "../../contexts/LiveChatContext";
import EngagedCustomerList from "./EngagedCustomerList";

/**
 * ChatList Component - Displays the sidebar with all conversations
 * Now includes tabs for Live Chat and Engaged Customers
 */
const ChatList = ({ searchQuery, setSearchQuery, onChatSelect }) => {
  const { chats, selectedChat, loading } = useLiveChat();
  const [activeTab, setActiveTab] = useState("live-chat");

  // Filter chats based on search
  const filteredChats = chats.filter(
    (chat) =>
      chat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chat.phone.includes(searchQuery)
  );

  return (
    <div className="w-full md:w-80 border-r border-gray-200 flex flex-col">
      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab("live-chat")}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === "live-chat"
              ? "text-primary border-b-2 border-primary bg-primary/5"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
          }`}
        >
          Live Chat
        </button>
        <button
          onClick={() => setActiveTab("engaged")}
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === "engaged"
              ? "text-primary border-b-2 border-primary bg-primary/5"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
          }`}
        >
          Engaged Customer
        </button>
      </div>

      {/* Search Bar */}
      <div className="p-3 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder={activeTab === "live-chat" ? "Search conversations..." : "Search engaged customers..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/40 focus:border-primary transition bg-white"
          />
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === "live-chat" ? (
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={30} className="animate-spin text-primary" />
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="text-center py-12 px-4">
              <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">
                {searchQuery ? "No conversations found" : "No conversations yet"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredChats.map((chat) => (
                <motion.div
                  key={chat.id}
                  onClick={() => onChatSelect(chat)}
                  className={`
                    p-3 cursor-pointer transition-colors duration-200
                    ${selectedChat?.id === chat.id ? "bg-primary/5" : "hover:bg-gray-50"}
                  `}
                  whileHover={{ backgroundColor: "rgba(0,0,0,0.02)" }}
                >
                  <div className="flex items-start gap-2.5">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User size={18} className="text-primary" />
                      </div>
                      {!chat.is_window_open && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full border-2 border-white"></div>
                      )}
                    </div>

                    {/* Chat Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {chat.name}
                        </h3>
                        <span className="text-[10px] text-gray-500 flex-shrink-0 ml-2">
                          {chat.timestamp}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-600 truncate">
                          {chat.lastMessage}
                        </p>
                        {chat.unread > 0 && (
                          <span className="flex-shrink-0 ml-2 bg-primary text-white text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                            {chat.unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <EngagedCustomerList
          searchQuery={searchQuery}
          onChatSelect={onChatSelect}
          selectedChat={selectedChat}
        />
      )}
    </div>
  );
};

export default ChatList;

